/**
 * @fileoverview Text File Editor Module Types and Configuration
 * 
 * Type definitions and validation for the Text File Editor Module
 */

/**
 * @typedef {Object} TextFileEditorConfig
 * @property {'create'|'edit'} mode - Editor mode
 * @property {string} [initialContent] - Initial content for the editor
 * @property {string} [initialFilename] - Initial filename
 * @property {number} maxLength - Maximum character limit (default: 50,000)
 * @property {string} placeholder - Placeholder text for empty editor
 * @property {boolean} autoSave - Enable auto-save functionality
 * @property {number} autoSaveInterval - Auto-save interval in milliseconds
 * @property {boolean} showCharacterCount - Show character count display
 * @property {boolean} showWordCount - Show word count display
 * @property {boolean} enableMarkdown - Enable markdown support
 * @property {boolean} enablePreview - Enable preview mode
 * @property {boolean} enableKeyboardShortcuts - Enable keyboard shortcuts
 * @property {string} saveEndpoint - API endpoint for saving files
 * @property {function} onSave - Callback when file is saved
 * @property {function} onCancel - Callback when editing is cancelled
 * @property {function} onContentChange - Callback when content changes
 * @property {function} onFilenameChange - Callback when filename changes
 * @property {function} onAutoSave - Callback when auto-save occurs
 * @property {Object} [metadata] - Additional metadata for the file
 * @property {string} [parrotId] - Parrot ID for context
 * @property {boolean} disabled - Whether the editor is disabled
 */

/**
 * @typedef {Object} TextFileContent
 * @property {string} filename - File name (without extension)
 * @property {string} content - File content
 * @property {number} characterCount - Number of characters
 * @property {number} wordCount - Number of words
 * @property {Date} lastModified - Last modification timestamp
 * @property {boolean} hasUnsavedChanges - Whether there are unsaved changes
 */

/**
 * @typedef {Object} AutoSaveState
 * @property {boolean} enabled - Whether auto-save is enabled
 * @property {number} interval - Auto-save interval in milliseconds
 * @property {Date} lastSave - Last auto-save timestamp
 * @property {boolean} saving - Whether auto-save is in progress
 * @property {string} [error] - Auto-save error message
 */

/**
 * @typedef {Object} EditorValidationResult
 * @property {boolean} valid - Whether validation passed
 * @property {string[]} errors - Validation errors
 * @property {string[]} warnings - Validation warnings
 */

/**
 * Default text file editor configuration
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6
 */
export const DEFAULT_TEXT_EDITOR_CONFIG = {
  mode: 'create',
  initialContent: '',
  initialFilename: '',
  maxLength: 50000, // 50,000 character limit per requirements 2.4
  placeholder: 'Enter your knowledge base content...',
  autoSave: true, // Auto-save functionality per requirements 2.3
  autoSaveInterval: 30000, // 30 seconds
  showCharacterCount: true, // Character count display per requirements 2.4
  showWordCount: true,
  enableMarkdown: true, // Markdown support per requirements 2.2
  enablePreview: true,
  enableKeyboardShortcuts: true, // Keyboard shortcuts per requirements 2.6
  saveEndpoint: '/api/modules/text-editor/save',
  disabled: false
};

/**
 * Keyboard shortcuts configuration
 */
export const KEYBOARD_SHORTCUTS = {
  save: { key: 's', ctrlKey: true, description: 'Save file (Ctrl+S)' },
  preview: { key: 'p', ctrlKey: true, description: 'Toggle preview (Ctrl+P)' },
  bold: { key: 'b', ctrlKey: true, description: 'Bold text (Ctrl+B)' },
  italic: { key: 'i', ctrlKey: true, description: 'Italic text (Ctrl+I)' },
  undo: { key: 'z', ctrlKey: true, description: 'Undo (Ctrl+Z)' },
  redo: { key: 'y', ctrlKey: true, description: 'Redo (Ctrl+Y)' }
};

/**
 * File name validation rules
 */
export const FILENAME_VALIDATION = {
  minLength: 1,
  maxLength: 100,
  allowedCharacters: /^[a-zA-Z0-9\s\-_\.]+$/,
  forbiddenNames: ['con', 'prn', 'aux', 'nul', 'com1', 'com2', 'com3', 'com4', 'com5', 'com6', 'com7', 'com8', 'com9', 'lpt1', 'lpt2', 'lpt3', 'lpt4', 'lpt5', 'lpt6', 'lpt7', 'lpt8', 'lpt9'],
  forbiddenExtensions: ['exe', 'bat', 'cmd', 'scr', 'pif', 'com']
};

/**
 * Content validation rules
 */
export const CONTENT_VALIDATION = {
  maxLength: 50000, // 50,000 character limit per requirements 2.4
  minLength: 0,
  allowEmptyContent: true
};

/**
 * Validate text editor configuration
 * 
 * @param {TextFileEditorConfig} config - Configuration to validate
 * @returns {EditorValidationResult} Validation result
 */
export function validateTextEditorConfig(config) {
  const errors = [];
  const warnings = [];

  // Check required fields
  if (!config) {
    errors.push('Configuration is required');
    return { valid: false, errors, warnings };
  }

  if (!config.mode) {
    errors.push('Mode is required');
  } else if (!['create', 'edit'].includes(config.mode)) {
    errors.push('Mode must be either "create" or "edit"');
  }

  if (typeof config.maxLength !== 'number' || config.maxLength <= 0) {
    errors.push('Max length must be a positive number');
  } else if (config.maxLength > CONTENT_VALIDATION.maxLength) {
    warnings.push(`Max length exceeds recommended limit of ${CONTENT_VALIDATION.maxLength} characters`);
  }

  if (!config.saveEndpoint || typeof config.saveEndpoint !== 'string') {
    errors.push('Save endpoint is required and must be a string');
  }

  if (typeof config.autoSave !== 'boolean') {
    errors.push('Auto save must be a boolean');
  }

  if (config.autoSave && (typeof config.autoSaveInterval !== 'number' || config.autoSaveInterval < 1000)) {
    errors.push('Auto save interval must be at least 1000ms when auto save is enabled');
  }

  // Optional field validations
  if (config.initialContent && typeof config.initialContent !== 'string') {
    errors.push('Initial content must be a string');
  }

  if (config.initialFilename && typeof config.initialFilename !== 'string') {
    errors.push('Initial filename must be a string');
  }

  if (config.placeholder && typeof config.placeholder !== 'string') {
    errors.push('Placeholder must be a string');
  }

  // Callback validations
  if (config.onSave && typeof config.onSave !== 'function') {
    errors.push('onSave must be a function');
  }

  if (config.onCancel && typeof config.onCancel !== 'function') {
    errors.push('onCancel must be a function');
  }

  if (config.onContentChange && typeof config.onContentChange !== 'function') {
    errors.push('onContentChange must be a function');
  }

  if (config.onFilenameChange && typeof config.onFilenameChange !== 'function') {
    errors.push('onFilenameChange must be a function');
  }

  if (config.onAutoSave && typeof config.onAutoSave !== 'function') {
    errors.push('onAutoSave must be a function');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Validate filename
 * 
 * @param {string} filename - Filename to validate
 * @returns {EditorValidationResult} Validation result
 */
export function validateFilename(filename) {
  const errors = [];
  const warnings = [];

  if (!filename || typeof filename !== 'string') {
    errors.push('Filename is required and must be a string');
    return { valid: false, errors, warnings };
  }

  const trimmed = filename.trim();
  
  if (trimmed.length < FILENAME_VALIDATION.minLength) {
    errors.push(`Filename must be at least ${FILENAME_VALIDATION.minLength} character long`);
  }

  if (trimmed.length > FILENAME_VALIDATION.maxLength) {
    errors.push(`Filename must be no more than ${FILENAME_VALIDATION.maxLength} characters long`);
  }

  if (!FILENAME_VALIDATION.allowedCharacters.test(trimmed)) {
    errors.push('Filename contains invalid characters. Only letters, numbers, spaces, hyphens, underscores, and dots are allowed');
  }

  // Check for forbidden names (case-insensitive)
  const lowerFilename = trimmed.toLowerCase();
  if (FILENAME_VALIDATION.forbiddenNames.includes(lowerFilename)) {
    errors.push(`"${trimmed}" is a reserved filename and cannot be used`);
  }

  // Check for forbidden extensions
  const extension = trimmed.split('.').pop()?.toLowerCase();
  if (extension && FILENAME_VALIDATION.forbiddenExtensions.includes(extension)) {
    errors.push(`Files with .${extension} extension are not allowed`);
  }

  // Warnings for potentially problematic names
  if (trimmed.startsWith('.')) {
    warnings.push('Filenames starting with a dot may be hidden on some systems');
  }

  if (trimmed.includes('..')) {
    warnings.push('Consecutive dots in filename may cause issues on some systems');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Validate content
 * 
 * @param {string} content - Content to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {EditorValidationResult} Validation result
 */
export function validateContent(content, maxLength = CONTENT_VALIDATION.maxLength) {
  const errors = [];
  const warnings = [];

  if (typeof content !== 'string') {
    errors.push('Content must be a string');
    return { valid: false, errors, warnings };
  }

  if (content.length > maxLength) {
    errors.push(`Content exceeds maximum length of ${maxLength} characters`);
  }

  // Warnings for potentially problematic content
  if (content.length > maxLength * 0.9) {
    warnings.push(`Content is approaching the maximum length limit (${content.length}/${maxLength} characters)`);
  }

  if (content.trim().length === 0 && content.length > 0) {
    warnings.push('Content appears to contain only whitespace');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Sanitize filename for safe storage
 * 
 * @param {string} filename - Filename to sanitize
 * @returns {string} Sanitized filename
 */
export function sanitizeFilename(filename) {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  return filename
    .trim()
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid characters
    .replace(/\s+/g, ' ') // Normalize whitespace
    .substring(0, FILENAME_VALIDATION.maxLength); // Truncate if too long
}

/**
 * Count words in content
 * 
 * @param {string} content - Content to count words in
 * @returns {number} Word count
 */
export function countWords(content) {
  if (!content || typeof content !== 'string') {
    return 0;
  }

  return content
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0)
    .length;
}

/**
 * Generate auto-save key for local storage
 * 
 * @param {string} context - Editor context
 * @param {string} [filename] - Optional filename
 * @returns {string} Auto-save key
 */
export function generateAutoSaveKey(context, filename = '') {
  const timestamp = Date.now();
  const key = filename ? `${context}-${filename}` : `${context}-new`;
  return `text-editor-autosave-${key}-${timestamp}`;
}

/**
 * Create text editor configuration
 * 
 * @param {'create'|'edit'} mode - Editor mode
 * @param {Partial<TextFileEditorConfig>} [overrides] - Configuration overrides
 * @returns {TextFileEditorConfig} Complete configuration
 */
export function createTextEditorConfig(mode, overrides = {}) {
  return {
    ...DEFAULT_TEXT_EDITOR_CONFIG,
    mode,
    ...overrides
  };
}

// Export the configuration class for JavaScript usage
export class TextFileEditorConfig {
  constructor(config = {}) {
    const merged = {
      ...DEFAULT_TEXT_EDITOR_CONFIG,
      ...config
    };
    Object.assign(this, merged);
  }
  
  validate() {
    return validateTextEditorConfig(this);
  }
}